import AuthService from "@/api/services/auth";
import UserService from "@/api/services/user";
import {
  isSeller,
  isResponsible,
  isOrganizationAdmin,
  isStoreAdmin,
  hasAccessToAdminDashboard,
  isManager,
  isRegionalManager,
} from "@/utils/user_roles";
import { isPresent } from "@estaly/ui";
import posthog from "posthog-js";
import { identifyPosthogUser } from "@/utils/posthog";

export default {
  namespaced: true,
  state: {
    currentUser: {},
    signedIn: false,
    csrf: null,
  },

  getters: {
    isMerchantProfile: (state) => state.currentUser.profileType === "MerchantProfile",
    isCustomer: (state) => state.currentUser.profileType === "Customer",
    isOrganizationAdmin: (state) => isOrganizationAdmin(state.currentUser),
    isStoreAdmin: (state) => isStoreAdmin(state.currentUser),
    isOrganizationOrStoreAdmin: (state) => isOrganizationAdmin(state.currentUser) || isStoreAdmin(state.currentUser),
    isSeller: (state) => isSeller(state.currentUser),
    isResponsible: (state) => isResponsible(state.currentUser),
    isManager: (state) => isManager(state.currentUser),
    isRegionalManager: (state) => isRegionalManager(state.currentUser),
    shouldShowAdminBrief: (state, getters) => {
      return (
        !getters.isRegionalManager &&
        (isOrganizationAdmin(state.currentUser) || isStoreAdmin(state.currentUser) || isManager(state.currentUser)) &&
        !getters.isWebOrganization
      );
    },
    currentUserRegion: (state) => state.currentUser?.region || null,
    isSellerOrResponsible: (state) => isSeller(state.currentUser) || isResponsible(state.currentUser),
    hasAccessToAdminDashboard: (state) => hasAccessToAdminDashboard(state.currentUser),
    isOffline: (state) => state.currentUser.storeSource === "offline",
    isOnline: (state) => state.currentUser.storeSource === "online",
    currentUserHasPermission: (state) => (permission) => {
      return isPresent(state.currentUser?.permissions) ? state.currentUser.permissions.includes(permission) : false;
    },
    hasRetailAnalytics: (state) => state.currentUser?.retailAnalytics || false,
    accessibleStoreIds: (state) => state.currentUser?.storeIds || [],
    isWebOrganization: (state) => state.currentUser?.organization?.source === "web",
    isRetailOrganization: (state) => state.currentUser?.organization?.source === "retail",
    organizationRegions: (state) => state.currentUser?.organization?.regions || [],
  },

  mutations: {
    setCurrentUser(state, { currentUser, csrf }) {
      state.currentUser = currentUser;
      state.signedIn = true;
      state.csrf = csrf;
    },

    unsetCurrentUser(state) {
      state.currentUser = {};
      state.signedIn = false;
      state.csrf = null;
    },

    refresh(state, csrf) {
      state.csrf = csrf;
      state.signedIn = true;
    },
  },

  actions: {
    async signin({ dispatch }, user) {
      const response = await AuthService.signin(user);
      if (!response?.success) return response;

      await dispatch("postSignIn", response);
      return response;
    },

    async signup({ dispatch }, params) {
      const response = await AuthService.signup(params);
      if (!response?.success) return response;

      await dispatch("postSignIn", response);
      return response;
    },

    async resetPassword({ dispatch }, { resetPasswordToken, payload }) {
      const response = await AuthService.resetPassword(resetPasswordToken, payload);
      if (!response?.success) return response;

      await dispatch("postSignIn", response);
      return response;
    },

    async postSignIn(context, response) {
      const csrf = response.data.csrf;
      if (csrf) {
        const resp = await UserService.getUser();
        const currentUser = resp.data;

        try {
          if (currentUser?.id && process.env.NODE_ENV === "production") {
            identifyPosthogUser(currentUser);
          }
        } catch (e) {
          // noop
        }

        context.commit("setCurrentUser", { currentUser, csrf });
        this.dispatch("cable/connect");
        if (context.getters["isSellerOrResponsible"]) {
          context.dispatch("store/updateStoreId", currentUser.storeIds[0], { root: true });
          context.dispatch("store/fetch", { id: currentUser.storeIds[0] }, { root: true });
          await context.dispatch("store/fetchActiveStores", { perPage: 2000 }, { root: true });
        }
        context.dispatch("notifications/clearAll", null, { root: true });
      } else {
        context.commit("unsetCurrentUser");
      }
    },

    async forgotPassword(_, email) {
      return await UserService.forgotPassword(email);
    },

    async refreshCurrentUser({ commit, state }) {
      const response = await UserService.getUser();
      if (!response?.success) return response;

      const currentUser = response.data;
      const csrf = state.csrf;
      commit("setCurrentUser", { currentUser, csrf });
      return response;
    },

    async signout({ commit }) {
      const response = await AuthService.signout();
      if (!response?.success) return response;

      try {
        if (process.env.NODE_ENV === "production") {
          posthog.reset();
        }
      } catch (e) {
        // noop
      }

      commit("unsetCurrentUser");
      this.dispatch("cable/disconnect");
      localStorage.removeItem("vuex_store");
      sessionStorage.clear();
      window.location.replace("/signin");
    },
  },
};
